import { z } from "zod";
import { uuidLoose } from "./uuid.js";
/**
 * Wire-format DTOs + request/response shapes for the admin Refunds endpoints
 * (`POST /v1/admin/refunds` + `GET /v1/admin/orders/:id/refunds`, P08-T7).
 *
 * Field naming: snake_case keys per spec §141 + the existing T2/T3/T5/T6
 * admin convention. The same shape is mirrored in:
 *   - request body (`CreateRefundBody`) — snake_case
 *   - response body (`RefundCreatedResponse`, `RefundsListResponse.items`) —
 *     snake_case
 *   - audit-log `afterJson`/`beforeJson` (RefundDTO + customer_email +
 *     order_id + order_total_minor) — snake_case
 *   - order_events.payload jsonb (`{refundId, amountMinor}`) — see
 *     RefundsService.settleFromWebhook (camelCase intentionally — it mirrors
 *     the webhook's other order_events.payload shapes; the storage convention
 *     for jsonb in this code path is camelCase)
 *   - queue payload (`OrderRefundIssuedMailJobData`) — snake_case verbatim
 *
 * Refund status mirrors `packages/db/src/schema/billing.ts:refundStatusValues`
 * which is the single source of truth. Re-exported via shared-types so the
 * admin UI typings can avoid pulling the db package.
 */
export const RefundStatusValues = [
    "pending",
    "succeeded",
    "failed",
    "cancelled",
];
export const RefundStatus = z.enum(RefundStatusValues);
/**
 * P18 — refund_kind discriminator. `standard` = buyer-money refund (the
 * pre-P18 default, against an existing payment). `cashback` = bank-credit
 * promotional reward (no Stripe call, settles on the next invoice via
 * cashback_credits ledger). Mirrors db enum
 * `packages/db/src/schema/billing.ts:refundKindValues`.
 */
export const refundKindValues = ["standard", "cashback"];
/**
 * P18 — cashback_category enum. Required on cashback refunds, NULL on
 * standard refunds. Mirrors db enum
 * `packages/db/src/schema/billing.ts:cashbackCategoryValues`.
 */
export const cashbackCategoryValues = [
    "goodwill",
    "promo",
    "loyalty",
    "resolution",
    "other",
];
/**
 * `POST /v1/admin/refunds` request body. All keys snake_case verbatim
 * (spec §141). `amount_minor` is REQUIRED — V1.0 has no "full refund"
 * shortcut: the admin UI always sends an explicit minor-units number so
 * partial refunds and full refunds have one code path. `reason` is free-form
 * up to 500 chars (mirrors `RejectCustomerBody.reason`); the storefront
 * UI surfaces it on the buyer's refund-issued email.
 *
 * NOTE: the plan body literal §141 calls this `CreateRefundRequest` but the
 * codebase convention is `*Body` for Zod request schemas (see
 * `FulfilOrderBody`, `RejectCustomerBody`, `UpdateCustomerBody`). Following
 * codebase convention per L1.
 */
export const CreateRefundBody = z.object({
    order_id: uuidLoose,
    amount_minor: z.number().int().positive(),
    reason: z.string().min(1).max(500),
});
/**
 * P18 — `POST /v1/admin/cashback` request body. Cashback-issuance is a
 * separate endpoint from standard refunds (no `order_id`, no `payment_id` —
 * the credit is company-scoped and settled on the next invoice via the
 * cashback_credits ledger). `amount_minor` is REQUIRED, positive integer;
 * `cashback_category` is one of the five `cashbackCategoryValues`;
 * `reason` mirrors `CreateRefundBody.reason` (1..500 chars).
 */
export const CreateCashbackBody = z.object({
    amount_minor: z.number().int().positive(),
    cashback_category: z.enum(cashbackCategoryValues),
    reason: z.string().min(1).max(500),
});
